<template>
  <vue-easy-lightbox
    :visible="LightboxStore.visible"
    :imgs="Images"
    :index="LightboxStore.index"
    :move-disabled="false"
    :preload="false"
    @on-index-change="setCurrIndex"
    @hide="LightboxStore.trigger(false)">
    <template #close-btn="{ close }">
      <div class="row justify-center q-mt-lg">
        <q-btn
          color="warning"
          text-color="black"
          label="fermer"
          :icon="symRoundedClose"
          @click="close" />
      </div>
    </template>
    <template #toolbar="{ toolbarMethods }">
      <div
        v-if="!isCurrentVideo"
        class="vel-toolbar">
        <div
          class="toolbar-btn toolbar-btn__zoomout"
          @click="toolbarMethods.zoomOut">
          <q-icon
            :name="symRoundedZoomOut"
            size="md" />
        </div>
        <div
          class="toolbar-btn toolbar-btn__zoomin"
          @click="toolbarMethods.zoomIn">
          <q-icon
            :name="symRoundedZoomIn"
            size="md" />
        </div>

        <div
          class="toolbar-btn toolbar-btn__resize"
          @click="toolbarMethods.resize">
          <q-icon
            :name="symRoundedAspectRatio"
            size="md" />
        </div>

        <div
          class="toolbar-btn toolbar-btn__rotate"
          @click="toolbarMethods.rotateLeft">
          <q-icon
            :name="symRoundedRotateLeft"
            size="md" />
        </div>

        <div
          class="toolbar-btn toolbar-btn__rotate"
          @click="toolbarMethods.rotateRight">
          <q-icon
            :name="symRoundedRotateRight"
            size="md" />
        </div>
      </div>
    </template>
    <template
      v-if="title || subtitle"
      #title>
      <div class="vel-img-title column items-center text-center q-pb-xl">
        <div
          v-if="title"
          class="text-h3">
          {{ title }}
        </div>
        <div
          v-if="subtitle"
          class="text-h5">
          {{ subtitle }}
        </div>
      </div>
    </template>
  </vue-easy-lightbox>

  <!-- Video slide: vue-easy-lightbox only renders <img>, so the player sits in
       an overlay above it. Container lets clicks pass through to the lightbox
       arrows/close; only the <video> itself captures the pointer. -->
  <div
    v-if="LightboxStore.visible && isCurrentVideo"
    class="lightbox-video-overlay">
    <video
      :key="currImage?.src"
      class="lightbox-video-player"
      :src="currImage?.src"
      :poster="currImage?.poster"
      controls
      autoplay
      playsinline />
  </div>
</template>

<script setup lang="ts">
import {
  symRoundedAspectRatio,
  symRoundedClose,
  symRoundedRotateLeft,
  symRoundedRotateRight,
  symRoundedZoomIn,
  symRoundedZoomOut,
} from '@quasar/extras/material-symbols-rounded'
import { type GalleryItem, useLightboxStore } from 'src/CrudManager/Core/stores/useLightboxStore'
import { computed, defineAsyncComponent } from 'vue'

// Async load vue-easy-lightbox component
const vueEasyLightbox = defineAsyncComponent(() => import('vue-easy-lightbox').then(m => m.default || m))

const LightboxStore = useLightboxStore()

// 16:9 dark placeholder used as the underlying lightbox slide for videos
// that don't have a poster frame yet (conversion still running).
const VIDEO_PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="1280" height="720"><rect width="100%" height="100%" fill="#1d1d1d"/></svg>')

// Remove the   title and subtitle from the images
const Images = computed(() => {
  return LightboxStore.images.map((img: GalleryItem) => {
    return {
      ...img,
      // Video slides show the poster (or a placeholder) as the zoomable img;
      // the actual player renders in the overlay above.
      src: img.type === 'video' ? img.poster || VIDEO_PLACEHOLDER : img.src,
      title: null,
      subtitle: null,
    }
  })
})

const currImage = computed(() => (LightboxStore.images ? LightboxStore.images[LightboxStore.index] : null))
const isCurrentVideo = computed(() => currImage.value?.type === 'video')

const setCurrIndex = (oldIndex: number, newIndex: number) => {
  LightboxStore.index = LightboxStore.images[newIndex] ? newIndex : oldIndex
  return oldIndex
}
const title = computed(() => (LightboxStore.hideTitle != 1 && currImage.value ? currImage.value.title : null))
const subtitle = computed(() => (LightboxStore.hideTitle != 1 && currImage.value ? currImage.value.subtitle : null))
</script>

<style lang="scss" scoped>
.lightbox-video-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000; // above .vel-modal (9998)
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none; // let clicks reach the lightbox arrows/close button
}

.lightbox-video-player {
  pointer-events: auto;
  max-width: min(90vw, 1280px);
  max-height: 70vh;
  background: #000;
  border-radius: 4px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.6);
}
</style>
